import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Lightweight autocomplete input that suggests previously-used tokens for the
 * given field across all animal records. For comma-separated fields the
 * suggestion completes only the LAST token in the input.
 */
export const AutocompleteInput = ({
  field,
  value,
  onChange,
  placeholder,
  multi = false,
  testid,
  className,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const ref = useRef(null);

  const lastToken = () => {
    if (!multi) return value || "";
    const parts = (value || "").split(/[,\n]/);
    return (parts[parts.length - 1] || "").trim();
  };

  useEffect(() => {
    const q = lastToken();
    if (!open) return undefined;
    let cancelled = false;
    axios
      .get(`${API}/autocomplete/${field}`, { params: { q } })
      .then((r) => !cancelled && setSuggestions(r.data.values || []))
      .catch(() => !cancelled && setSuggestions([]));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, open, field]);

  const pick = (v) => {
    if (!multi) {
      onChange(v);
    } else {
      const parts = (value || "").split(/[,\n]/);
      parts[parts.length - 1] = " " + v;
      onChange(parts.join(",").replace(/^,\s*/, "").trim());
    }
    setOpen(false);
  };

  return (
    <div className={`relative ${className || ""}`} ref={ref}>
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 160)}
        placeholder={placeholder}
        data-testid={testid}
        disabled={disabled}
      />
      {open && suggestions.length > 0 && (
        <ul
          className="absolute z-50 left-0 right-0 mt-1 max-h-52 overflow-y-auto bg-white border border-[var(--app-border)] rounded-md shadow-lg text-sm"
          data-testid={`${testid}-suggestions`}
        >
          {suggestions.map((s) => (
            <li
              key={s}
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
              className="px-3 py-1.5 cursor-pointer hover:bg-[var(--app-bg)]"
            >
              {s}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AutocompleteInput;
